import {useEffect, useState} from 'react';
import {gql, useQuery} from '@apollo/client';

import {parsePriceData, PriceQueryResult, PriceResult, UsePricesOptions} from './types';

/**
 * usePrices hook (unified)
 *
 * Fetches and returns all price data for a product, supporting all scenarios handled by both usePrices and useBulkPrices.
 *
 * @param options - Options object. All fields are optional:
 *   - name?: string (defaults to 'DEXA')
 *   - apptId?: string
 *   - region?: string
 *   - bulkQty?: number
 *   - disc?: string
 *
 * @returns PriceResult:
 *   - id, shortName
 *   - memberships: Membership (by renew interval)
 *   - oneTime: number[] (one-time prices)
 *   - basePrices: { quantity, total }[] (bulk pricing)
 *   - discPrices: { quantity, total }[] (discounted bulk pricing)
 *   - isLoading: boolean
 *   - hasError: Error | undefined
 */
export const usePrices = (
	options: UsePricesOptions = {}
): PriceResult => {
	const {
		name = 'DEXA',
		apptId,
		region,
		bulkQty,
		disc,
	} = options;
	const query = gql`
		query (
			$region: String
			$apptId: String
			$bulkQty: Int
			$discount: String
		) {
			products {
				edges {
					node {
						id
						shortName
						prices: prices(region: $region, apptId: $apptId) {
							memberships {
								price {
									quantity
									total
								}
								renewInterval
							}
							prices {
								quantity
								total
							}
						}
						base: prices(region: $region, apptId: $apptId, bulkQty: $bulkQty) {
							prices {
								quantity
								total
							}
						}
						disc: prices(region: $region, apptId: $apptId, bulkQty: $bulkQty, discount: $discount) {
							prices {
								quantity
								total
							}
						}
					}
				}
			}
		}
	`;

	const {loading, error, data} = useQuery<PriceQueryResult>(query, {
		variables: {
			...(region ? {region} : {}),
			...(apptId ? {apptId} : {}),
			...(typeof bulkQty === 'number' ? {bulkQty} : {}),
			...(disc ? {discount: disc} : {}),
		},
	});
	const [result, setResult] = useState<Omit<PriceResult, 'isLoading' | 'hasError'> | null>(null);
	const [hasError, setHasError] = useState<Error | undefined>(undefined);

	useEffect(() => {
		if (loading) {
			setResult(null);
			setHasError(undefined);
			return;
		}
		if (error) {
			setResult(null);
			setHasError(error);
			return;
		}
		if (!data) {
			setResult(null);
			setHasError(new Error('No data returned from price query'));
			return;
		}
		const match = data.products.edges.find(
			(edge: PriceQueryResult['products']['edges'][number]) => edge.node && edge.node.shortName === name
		);
		if (match && match.node) {
			setResult(parsePriceData(match.node));
			setHasError(undefined);
		} else {
			setResult(null);
			setHasError(new Error('No prices for product: ' + name));
		}
	}, [loading, error, data, name]);

	return {
		id: result?.id ?? '',
		shortName: result?.shortName ?? name,
		memberships: result?.memberships ?? {MONTH: [], QUARTER: []},
		oneTime: result?.oneTime ?? [],
		basePrices: result?.basePrices ?? [],
		discPrices: result?.discPrices ?? [],
		isLoading: loading || result === null || hasError !== undefined,
		hasError,
	};
};
